import {FieldInputProps} from 'formik';
import {uniqBy} from 'lodash-es';
import {BuilderPot} from '../../../schema-types/BuilderPot';
import {mainPlanTypeType} from '../CreateBuilder/builderFields';
import {transferFieldsType} from './builderTransferFields';
import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';
import {getPotInfo, PotInfo} from '../../../utils/getPotInfo';
import {reccNonEditable} from '../CreateBuilder/BuilderSourceDestination/CreateBuilderFooter';
import {POTS_ANNUITY, POTS_PENSION} from '../../../utils/potsFilter';

export const transferSourcePots = ({
  sourcePots,
  builderDetails,
  mainPlanType,
  contact,
  showAllPensions,
}: {
  mainPlanType: mainPlanTypeType;
  sourcePots: BuilderPot[];
  builderDetails: OvbuilderDetails_ovrecommendDetails | null;
  contact?: string;
  showAllPensions?: boolean;
}): BuilderPot[] => {
  const destPot = builderDetails?.pots?.find((x) => x.planType === 'destination');
  return uniqBy(sourcePots, 'id').filter((pot) => {
    if (pot.isDummy) {
      const potInfo = getPotInfo(pot, builderDetails?.id);

      if (pot.originalOvrId && builderDetails?.id) {
        // return only new plans pertinent to this rec

        if (destPot && pot.id === destPot.pot.id) {
          return false; // if destination is new pot, dont show in sources
        }

        if (potInfo === PotInfo.existingShellPlanThisRec) {
          return false; // show as shell plan if the shell plan was made in this recc
        }
      }
      if (!pot.isShellPlan) {
        // if not shell plan, return false
        return false;
      }
    }
    if (mainPlanType === POTS_PENSION && !showAllPensions) {
      return pot.contact?.id === contact;
    }
    if (mainPlanType === POTS_ANNUITY) {
      return pot.productName?.acronym === 'Pension';
    }
    return true;
  });
};

export interface TransferShellPlanData {
  id: string;
  accountNumber?: string | null;
  planProvider?: string | null;
}

export const transferShellPlans = ({
  transferSources,
}: {
  transferSources: transferFieldsType['transferSources'] | undefined;
}): TransferShellPlanData[] => {
  return Object.values(transferSources || {})
    .filter((x) => x.isShellPlan && x.checked)
    .sort((a, b) => {
      if (a.planProvider?.includes('saltus')) {
        return -1;
      }
      if (b.planProvider?.includes('saltus')) {
        return -1;
      }
      return 0;
    });
};

export const getUnselectedTransferSources = ({
  transferSourcesFieldObj,
  sourcePots,
  builderDetails,
}: {
  transferSourcesFieldObj: FieldInputProps<
    transferFieldsType['transferSources'] | undefined
  >;
  sourcePots: BuilderPot[];
  builderDetails: OvbuilderDetails_ovrecommendDetails | null;
}): BuilderPot[] => {
  // keys may be: child pot id, leaf parent pot id, or `shell-N`
  const selectedSourceIds = Object.keys(transferSourcesFieldObj.value ?? {});

  // a destination parent is excluded if either
  //   - the parent itself is in selectedSourceIds (leaf-parent source), OR
  //   - any of its dentistChildren are in selectedSourceIds (child source under that parent)
  const isParentOrAnyChildSelected = (p: BuilderPot): boolean => {
    if (selectedSourceIds.includes(p.id)) return true;
    return (p.dentistChildren ?? []).some((c) => selectedSourceIds.includes(c.id));
  };

  return sourcePots
    .filter((p) => !isParentOrAnyChildSelected(p))
    .filter((p) => {
      if (builderDetails?.id && p.isDummy && p.originalOvrId === builderDetails.id) {
        return false;
      }
      return true;
    });
};

export const transferDestinationPots = ({
  transferSourcesFieldObj,
  sourcePots,
  builderDetails,
  mainPlanType,
  isSppDa,
  showAllPensions,
  contact,
}: {
  transferSourcesFieldObj: FieldInputProps<
    transferFieldsType['transferSources'] | undefined
  >;
  sourcePots: BuilderPot[];
  builderDetails: OvbuilderDetails_ovrecommendDetails | null;
  mainPlanType: mainPlanTypeType;
  isSppDa: boolean;
  showAllPensions?: boolean;
  contact?: string;
}): BuilderPot[] => {
  const filteredSourcePots = sourcePots.filter((pot) => {
    if (mainPlanType === POTS_PENSION && !showAllPensions) {
      return pot.contact?.id === contact;
    }
    return true;
  });
  const unselectedSources = getUnselectedTransferSources({
    transferSourcesFieldObj,
    sourcePots: filteredSourcePots,
    builderDetails,
  });

  // Top-level destination pots already saved in this recommendation (excludes subplans)
  const selected =
    builderDetails?.pots
      ?.filter((x) => x.planType === 'destination' && !x.pot.parent?.id)
      .map((x) => x.pot) || [];

  const destinationPlans =
    builderDetails?.pots?.filter((x) => x.planType === 'destination').map((x) => x.pot) ||
    [];

  const potsToUse = reccNonEditable(builderDetails)
    ? destinationPlans
    : unselectedSources;

  // Combine allowed pots with already-saved destinations, excluding dummy pots
  // that were created as new plans for this specific recommendation
  const sources = [...potsToUse, ...selected].filter((x) => {
    if (x.isDummy && x.originalOvrId && x.originalOvrId === builderDetails?.id) {
      return false;
    }
    return true;
  }) as BuilderPot[];

  return uniqBy(sources, 'id').filter((x) => {
    if (isSppDa) {
      return x.isSaltus;
    }
    return true;
  });
};

export const getTransferDestinationFixed = (
  builderDetails: OvbuilderDetails_ovrecommendDetails | null
): boolean => {
  const selected =
    builderDetails?.pots?.filter(
      (x) => x.planType === 'destination' && !x.pot.parent?.id
    ) || [];
  return selected.length > 0;
};
